
import React, { useContext, useEffect, useState } from 'react';
import { WeeklyShiftForecast } from '../../types/api/WeeklyShiftForecast';
import { Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Formation from '../../types/api/Formation';
import { DailyClientForecast } from '../../types/api/DailyClientForecast';
import { getWeekNumber } from '../../tools/utils';
import { deletePlanningForecastWeek, deleteWeeklyPlanningForecast, listEmployees, upsertWeeklyPlanningForecast } from '../../api/restaurant';
import EditIcon from '@mui/icons-material/Edit';
import CachedIcon from '@mui/icons-material/Cached';
import { AppContext } from '../../context/appContext';
import HourSelection from '../Inputs/HourSelection';
import Employee from '../../types/api/Employee';
import YesNoPopup from '../Popups/YesNoPopup';
import { PREDEFINED_SHIFTS, PREDEFINED_SHIFTS_VALUES } from '../../tools/constants';
import GenerateForecast from '../Popups/GenerateForecast';
import { RegenerateForecastOption } from '../../types/api/RegenerateForecastOption';

interface WeeklyShiftsForecastTableProps {
    selectedWeek: Date | null;
    shifts: WeeklyShiftForecast[]
    clientForecasts: DailyClientForecast[]
    formations: Formation[];
    refreshWeeklyShiftsForecasts: () => Promise<void>;
    regenerateWeeklyShiftsForecasts: ((options: RegenerateForecastOption) => Promise<void>) | null;
}

// Map database day names to array indices
const dayMapping: { [key: string]: number } = {
    'MONDAY': 0,
    'TUESDAY': 1,
    'WEDNESDAY': 2,
    'THURSDAY': 3,
    'FRIDAY': 4,
    'SATURDAY': 5,
    'SUNDAY': 6
};

function WeeklyShiftsForecastTable(WeeklyShiftsForecastTableProps: WeeklyShiftsForecastTableProps) {
    const theme = useTheme();
    const appContext = useContext(AppContext);
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const [uniqueFormationsCombination, setUniqueFormationsCombination] = React.useState<string[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);

    const updateEmployees = async () => {
        const res = await listEmployees();
        if (res && res.code === 200 && res.data) {
            //console.log(res.data)
            setEmployees(res.data);
        }
    };
    
    useEffect(() => {
        updateEmployees();
    }, []);


    useEffect(() => {
        const formationsSet = new Set<string>();
        WeeklyShiftsForecastTableProps.shifts.forEach(shift => {
            const formationLabels = shift.formations.map((formation) => (formation as Formation).id).sort().join(',');
            formationsSet.add(formationLabels);
        }
        );
        const formationsIds = Array.from(formationsSet).map(labelStr => {
            const ids = labelStr.split(',');
            return ids.join(', ')
        });
        setUniqueFormationsCombination(formationsIds); // Separated by comma
    }, [WeeklyShiftsForecastTableProps.shifts]);


    const getDayOfWeek = (dayIndex: number): Date | null => {
        if (!WeeklyShiftsForecastTableProps.selectedWeek) {
            return null;
        }
        const date = new Date((WeeklyShiftsForecastTableProps.selectedWeek as Date).getTime());
        // Get Monday of current week
        const currentDay = date.getDay();
        const diff = currentDay === 0 ? -6 : 1 - currentDay;
        date.setDate(date.getDate() + diff);
        // Add dayIndex to get specific day
        date.setDate(date.getDate() + dayIndex);
        return date;
    };

    // Get shifts for a specific day
    const getShiftsForDayAndFormations = (dayIndex: number, formations: string): WeeklyShiftForecast[] => {
        const dayName = Object.keys(dayMapping).find(key => dayMapping[key] === dayIndex) || '';
        return WeeklyShiftsForecastTableProps.shifts.filter(
            shift => shift.day === dayName && shift.formations.map((formation) => (formation as Formation).id).sort().join(', ') === formations
        );
    };

    const shiftCell = (shift: WeeklyShiftForecast) => (
        <Paper 
            key={shift.id}
            sx={{ 
                padding: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                position: 'relative',
                backgroundColor: `${(shift.formations?.[0] as Formation)?.color ?? '#000'}20`
            }}
        >
            {shift.shiftName && (
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                    {shift.shiftName}
                </Typography>
            )}
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                {shift.start}H - {shift.end === 25 ? 'F' : shift.end + 'H'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                ({shift.duration}h)
            </Typography>
            {shift.isDouble && (
                <Typography variant="caption" sx={{ display: 'block', marginTop: 0.5, fontStyle: 'italic' }}>
                    + {shift.doubleStart}H - {shift.doubleEnd === 25 ? 'F' : shift.doubleEnd + 'H'} ({shift.doubleDuration}h)
                </Typography>
            )}

            {shift.preassignedEmployees?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                        Pré-assigné à:
                    </Typography>
                    {shift.preassignedEmployees.map((emp, idx) => (
                        <Typography
                            key={emp.id ?? `${emp.firstName}-${emp.lastName}-${idx}`}
                            variant="caption"
                            sx={{
                                color: theme.palette.text.secondary,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: `${theme.palette.text.primary}0d`
                            }}
                        >
                            {emp.firstName} {emp.lastName}
                        </Typography>
                    ))}
                </Box>
            )}
            <Box 
                onClick={() => handleDeleteShift(shift.id)}
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    cursor: 'pointer',
                    '&:hover': { color: theme.palette.error.main }
                }}
            >
                <DeleteIcon sx={{ fontSize: '0.875rem' }} />
            </Box>
            <Box 
                onClick={() => handleOpenEditShiftDialog(shift)}
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 24,
                    cursor: 'pointer',
                    '&:hover': { color: theme.palette.primary.main }
                }}
            >
                <EditIcon sx={{ fontSize: '0.875rem' }} />
            </Box>
        </Paper>
    );


    // Delete weekly forecasts Dialog
    const [openDeleteWeeklyForecastsDialog, setOpenDeleteWeeklyForecastsDialog] = useState(false);

    const handleOpenDeleteWeeklyForecastsDialog = () => {
        setOpenDeleteWeeklyForecastsDialog(true);
    }

    const deleteWeeklyForecasts = async () => {
        const res = await deletePlanningForecastWeek(
            WeeklyShiftsForecastTableProps.selectedWeek?.getFullYear() ?? 0,
            getWeekNumber(WeeklyShiftsForecastTableProps.selectedWeek as Date)
        )
        if (res && res.code === 200 ) {
            appContext.showSnackbar('Prévisions hebdomadaires supprimées avec succès.', 'success');
        }else{
            appContext.showSnackbar("Une erreur est survenue lors de la suppression des prévisions hebdomadaires.", 'error');
        }
        WeeklyShiftsForecastTableProps.refreshWeeklyShiftsForecasts()
        setOpenDeleteWeeklyForecastsDialog(false);
    }


    // New Shift Dialog Handlers
    const [openShiftDialog, setOpenShiftDialog] = useState(false);
    const [newShift, setNewShift] = useState({
        id: -1,
        formations: [],
        start: 8,
        end: 15,
        duration: 7,
        isDouble: false,
        doubleStart: 18,
        doubleEnd: 23,
        doubleDuration: 5,
        year: WeeklyShiftsForecastTableProps.selectedWeek?.getFullYear() ?? null,
        weekNumber: WeeklyShiftsForecastTableProps.selectedWeek ? getWeekNumber(WeeklyShiftsForecastTableProps.selectedWeek) : null,
        day: 'MONDAY',
        preassignedEmployees: [],
        shiftName: null
    } as WeeklyShiftForecast);
    

    const handleOpenEditShiftDialog = (shift: WeeklyShiftForecast) => {
        setNewShift(shift);
        setOpenShiftDialog(true);
    }

    const handleOpenShiftDialog = (dayName: string) => {
        setNewShift({
            ...newShift,
            day: dayName as any
        })
        setOpenShiftDialog(true);
    };

    const handleCloseShiftDialog = () => {
        setOpenShiftDialog(false);
        resetNewWeeklyShift();
    };

    const handleShiftChange = (field: string, value: any) => {
        const updatedShift = { ...newShift, [field]: value };

        if(field === 'start' || field === 'end' || field === 'doubleStart' || field === 'doubleEnd') {
            if (updatedShift.isDouble) {
                updatedShift.doubleDuration = updatedShift.doubleEnd - updatedShift.doubleStart;
                if(updatedShift.doubleEnd === 25) updatedShift.doubleDuration -= 1;
            }

            updatedShift.duration = updatedShift.end - updatedShift.start;
            if(updatedShift.end === 25) updatedShift.duration -= 1;

            if(updatedShift.isDouble) updatedShift.duration += updatedShift.doubleDuration;
        }
        

        
        setNewShift(updatedShift);
    };

    const handlePredefinedShift = (type: PREDEFINED_SHIFTS) => {
        const shift_to_override_with = PREDEFINED_SHIFTS_VALUES[type]
        setNewShift({
            ...newShift,
            ...shift_to_override_with,
            
        });
    };
    
    const handleSaveShift = async () => {
        if(newShift.formations.length === 0) {
            appContext.showSnackbar('Veuillez sélectionner au moins une formation.', 'error');
            return;
        }
        try {
            const res = await upsertWeeklyPlanningForecast(newShift)
            if (res && res.code === 200) {
                console.log('Shift saved successfully');
            }
            await WeeklyShiftsForecastTableProps.refreshWeeklyShiftsForecasts();

        } catch (e) {
            console.error(e);
        }finally {
            handleCloseShiftDialog();
        }
    };

    const resetNewWeeklyShift = () => {
        setNewShift({
            id: -1,
            formations: [],
            start: 8,
            end: 15,
            duration: 7,
            isDouble: false,
            doubleStart: 18,
            doubleEnd: 23,
            doubleDuration: 5,
            year: WeeklyShiftsForecastTableProps.selectedWeek?.getFullYear() ?? null,
            weekNumber: WeeklyShiftsForecastTableProps.selectedWeek ? getWeekNumber(WeeklyShiftsForecastTableProps.selectedWeek) : null,
            day: 'MONDAY',
            preassignedEmployees: [],
            shiftName: null
        } as WeeklyShiftForecast);
    }

    // Delete Shift Handler
    const handleDeleteShift = async (shiftId: number) => {
        try {
            const res = await deleteWeeklyPlanningForecast(shiftId)
            if (res && res.code === 200) {
                console.log('Shift deleted successfully');
            }
            await WeeklyShiftsForecastTableProps.refreshWeeklyShiftsForecasts();
        } catch (e) {
            console.error(e);
        }
    }


    const [openRegenerateForecastDialog, setOpenRegenerateForecastDialog] = useState(false);
    const [regenerateForecastOption, setRegenerateForecastOption] = useState<RegenerateForecastOption>({ 
        weeklyNoonClientForecast: [], 
        weeklyEveningClientForecast: [] 
    });
    const handleOpenRegenerateForecastDialog = () => {
        setRegenerateForecastOption({
            weeklyNoonClientForecast: WeeklyShiftsForecastTableProps.clientForecasts.map((f) => f.avgNoonClients ? Math.round(f.avgNoonClients) : 0),
            weeklyEveningClientForecast: WeeklyShiftsForecastTableProps.clientForecasts.map((f) => f.avgEveningClients ? Math.round(f.avgEveningClients) : 0)
        })
        setOpenRegenerateForecastDialog(true);
    }
    const handleCancelRegenerateForecast = () => {
        setOpenRegenerateForecastDialog(false);
    }
    const handleValidateRegenerateForecast = async (finalOptions: RegenerateForecastOption) => {
        if(WeeklyShiftsForecastTableProps.regenerateWeeklyShiftsForecasts){
            await WeeklyShiftsForecastTableProps.regenerateWeeklyShiftsForecasts(finalOptions);
            setOpenRegenerateForecastDialog(false);
        }else{
            appContext.showSnackbar("La régénération des prévisions hebdomadaires n'est pas disponible.", 'error');
        }
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Paper 
                    sx={{padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                    elevation={0}
                >
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
                        <Typography>

                        </Typography>

                        {WeeklyShiftsForecastTableProps.regenerateWeeklyShiftsForecasts && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Button
                                    variant='contained'
                                    color='error'
                                    startIcon={<DeleteIcon />}
                                    onClick={handleOpenDeleteWeeklyForecastsDialog}
                                >
                                    Supprimer
                                </Button>
                                <Button
                                    variant='contained'
                                    sx={{
                                        color: theme.palette.primary.contrastText
                                    }}
                                    startIcon={<CachedIcon />}
                                    onClick={handleOpenRegenerateForecastDialog}
                                >
                                    Regénérer
                                </Button>
                            </div>
                        )}
                    </Stack>
                </Paper>

                {/* Header Row - Days of the week */}
                <Box sx={{ 
                    display: 'flex', 
                    width: '100%', 
                    borderBottom: `2px solid ${theme.palette.divider}`, 
                    minHeight: '120px',
                    alignItems: 'stretch',
                }}>
                    <Box sx={{ 
                        width: '150px', 
                        borderRight: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            textAlign: 'center',
                            color: "text.primary"
                            
                        }}>
                            Formation(s)
                        </Typography>
                    </Box>

                    {
                        weekDays.map((dayName, index) => {
                            const date = getDayOfWeek(index);
                            const isToday = date ? new Date().toDateString() === date.toDateString() : false;
                            return (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        flex: 1, 
                                        padding: 1.5, 
                                        textAlign: 'center',
                                        borderLeft: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                                        backgroundColor: isToday ? `${theme.palette.primary.main}08` : "background.paper",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                                            textAlign: 'center',
                                            color: isToday ? "primary.main" : "text.primary",
                                            marginBottom: 1,

                                        }}
                                    >
                                        {dayName.toUpperCase()}
                                        {(() => {
                                            const dayForecast = WeeklyShiftsForecastTableProps.clientForecasts.find(fc => fc.day === index);
                                            if (!dayForecast) return null;

                                            return (
                                                <Tooltip
                                                    arrow
                                                    title={
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                            <Typography variant="caption" sx={{ color: 'inherit' }}>
                                                                MIDI: {dayForecast.avgNoonClients || 0}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'inherit' }}>
                                                                SOIR: {dayForecast.avgEveningClients || 0}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            alignSelf: 'center',
                                                            color: theme.palette.text.secondary,
                                                            mb: 0.5,
                                                            p: 0.25
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            );
                                        })()}
                                    </Typography>
                                    {WeeklyShiftsForecastTableProps.clientForecasts.length > 0 && WeeklyShiftsForecastTableProps.clientForecasts.find(fc => fc.day === index) && (
                                        <>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                MIDI: {WeeklyShiftsForecastTableProps.clientForecasts.find(fc => fc.day === index)?.avgNoonClients || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                SOIR: {WeeklyShiftsForecastTableProps.clientForecasts.find(fc => fc.day === index)?.avgEveningClients || 0}
                                            </Typography>
                                        </>
                                    )}
                                    {date && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: isToday ? "primary.main" : "text.secondary",
                                                fontWeight: isToday ? 'bold' : 'normal',
                                                marginBottom: 1.5
                                            }}
                                        >
                                            {date.getDate()} {new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(date)}
                                        </Typography>
                                    )}
                                    <Button
                                        onClick={() => handleOpenShiftDialog(Object.keys(dayMapping).find(key => dayMapping[key] === index) || '')}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            backgroundColor: "background.paper",
                                            borderColor: "divider",
                                            color: "text.primary",
                                            fontSize: '0.75rem',
                                            padding: '4px 8px',
                                            minHeight: '32px',
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: '6px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: "primary.main",
                                                borderColor: "primary.main",
                                                color: "primary.contrastText",
                                                transform: 'translateY(-1px)',
                                                boxShadow: theme.shadows[2]
                                            }
                                        }}
                                    >
                                        + Ajouter
                                    </Button>
                                </Box>
                            );
                        })
                    }
                </Box>

                {/* Formation Combination Rows */}
                {uniqueFormationsCombination.map((item, rowIndex) => {
                    // Get formation labels for display
                    const formationLabels = WeeklyShiftsForecastTableProps.shifts
                        .find(shift => shift.formations.map((formation) => (formation as Formation).id).sort().join(',') === item.replace(/\s/g, ''))
                        ?.formations.map((formation) => (formation as Formation).label).join(', ') || 'Unknown';

                    return (
                        <Box 
                            key={`row-${rowIndex}`} 
                            sx={{ 
                                display: 'flex', 
                                width: '100%', 
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                minHeight: '80px',
                                alignItems: 'stretch'
                            }}
                        >
                            {/* Formation Label Column */}
                            <Box sx={{ 
                                width: '150px', 
                                border: `1px solid ${theme.palette.divider}`,
                                color: "text.primary",
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', color: "text.primary" }}>
                                    {formationLabels}
                                </Typography>
                            </Box>
                            
                            {/* Day Columns */}
                            {weekDays.map((dayName, dayIndex) => {
                                const shifts = getShiftsForDayAndFormations(dayIndex, item);
                                const noonShifts = shifts.filter(shift => shift.start < 16 && !shift.isDouble);
                                const doubleShifts = shifts.filter(shift => shift.isDouble);
                                const eveningShifts = shifts.filter(shift => shift.start >= 16 && !shift.isDouble);

                                return (
                                    <Box 
                                        key={`${item}-${dayIndex}`} 
                                        sx={{ 
                                            flex: 1, 
                                            padding: 1, 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: 0.5,
                                            borderLeft: dayIndex > 0 ? `3px solid ${theme.palette.divider}` : 'none',
                                            minHeight: '100%'
                                        }}
                                    >
                                        {noonShifts.length > 0 && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                                                    MIDI
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {noonShifts.map(shiftCell)}
                                                </Box>
                                            </Box>
                                        )}
                                        
                                        {doubleShifts.length > 0 && (
                                            <Box sx={{ mb: 1, borderTop: noonShifts.length > 0 ? `1px dashed ${theme.palette.divider}` : 'none', pt: noonShifts.length > 0 ? 1 : 0 }}>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                                                    DOUBLE
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {doubleShifts.map(shiftCell)}
                                                </Box>
                                            </Box>
                                        )}

                                        {eveningShifts.length > 0 && (
                                            <Box sx={{ borderTop: (noonShifts.length > 0 || doubleShifts.length > 0) ? `1px dashed ${theme.palette.divider}` : 'none', pt: (noonShifts.length > 0 || doubleShifts.length > 0) ? 1 : 0 }}>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                                                    SOIR
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {eveningShifts.map(shiftCell)}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>



            {/* Add Shift Dialog */}
            <Dialog 
                open={openShiftDialog} 
                onClose={handleCloseShiftDialog} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Ajouter un shift - {newShift.day}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Formation</InputLabel>
                            <Select
                                multiple
                                value={newShift.formations.map((f) => (f as Formation).id)}
                                label="Formation"
                                onChange={(e) => {
                                    const selectedIds = e.target.value as number[];
                                    const selectedFormations = WeeklyShiftsForecastTableProps.formations.filter((f) => selectedIds.includes(f.id));
                                    handleShiftChange('formations', selectedFormations);
                                }}
                            >
                                {WeeklyShiftsForecastTableProps.formations.map((formation) => (
                                    <MenuItem key={formation.id} value={formation.id}>
                                        {formation.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <Autocomplete
                                multiple
                                options={employees}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                value={newShift.preassignedEmployees}
                                onChange={(event, value) => {
                                    const next = value.slice(-1); // enforce single selection while keeping multiple API
                                    handleShiftChange('preassignedEmployees', next);
                                }}
                                limitTags={1}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Employé(s) pré-assigné(s)"
                                        placeholder="Rechercher..."
                                    />
                                )}
                            />
                        </FormControl>


                        <FormControl>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Type de shift :</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newShift.shiftName === null}
                                            onChange={() => handlePredefinedShift(PREDEFINED_SHIFTS.CUSTOM)}
                                        />
                                    }
                                    label="Personalisable"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newShift.shiftName === PREDEFINED_SHIFTS.DAY}
                                            onChange={() => handlePredefinedShift(PREDEFINED_SHIFTS.DAY)}
                                        />
                                    }
                                    label="Journée"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newShift.shiftName === PREDEFINED_SHIFTS.DOUBLE}
                                            onChange={() => handlePredefinedShift(PREDEFINED_SHIFTS.DOUBLE)}
                                        />
                                    }
                                    label="Double"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newShift.shiftName === PREDEFINED_SHIFTS.EVENING}
                                            onChange={() => handlePredefinedShift(PREDEFINED_SHIFTS.EVENING)}
                                        />
                                    }
                                    label="Soirée"
                                />
                            </Box>
                        </FormControl>

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginTop: 1 }}>
                            Premier shift
                        </Typography>

                        <HourSelection
                            disabled={newShift.shiftName != null}
                            label="Heure de début"
                            selectedHour={newShift.start}
                            setSelectedHour={(data)=>{handleShiftChange('start', data.target.value as number)}}
                            min={8}
                            max={newShift.end ? newShift.end - 3 : 24}
                        />
                        <HourSelection
                            disabled={newShift.shiftName != null}
                            label="Heure de fin"
                            selectedHour={newShift.end}
                            setSelectedHour={(data)=>{handleShiftChange('end', data.target.value as number)}}
                            min={newShift.start ? newShift.start + 3 : 8}
                            max={25}
                        />

                        <TextField
                            label="Durée (heures)"
                            type="number"
                            value={newShift.duration}
                            disabled
                            fullWidth
                        />

                        <FormControl>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="checkbox"
                                    checked={newShift.isDouble}
                                    onChange={(e) => handleShiftChange('isDouble', e.target.checked)}
                                    id="isDouble-checkbox"
                                />
                                <label htmlFor="isDouble-checkbox">
                                    <Typography variant="body2">Ajouter un deuxième shift</Typography>
                                </label>
                            </Box>
                        </FormControl>

                        {newShift.isDouble && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginTop: 1 }}>
                                    Deuxième shift
                                </Typography>

                                <HourSelection
                                    disabled={newShift.shiftName != null}
                                    label="Heure de début"
                                    selectedHour={newShift.doubleStart}
                                    setSelectedHour={(data)=>{handleShiftChange('doubleStart', data.target.value as number)}}
                                    min={8}
                                    max={newShift.doubleEnd ? newShift.doubleEnd - 3 : 24}
                                />
                                <HourSelection
                                    disabled={newShift.shiftName != null}
                                    label="Heure de fin"
                                    selectedHour={newShift.doubleEnd}
                                    setSelectedHour={(data)=>{handleShiftChange('doubleEnd', data.target.value as number)}}
                                    min={newShift.doubleStart ? newShift.doubleStart + 3 : 8}
                                    max={25}
                                />

                                <TextField
                                    label="Durée (heures)"
                                    type="number"
                                    value={newShift.doubleDuration}
                                    disabled
                                    fullWidth
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseShiftDialog}>Annuler</Button>
                    <Button onClick={handleSaveShift} variant="contained" color="primary">
                        Sauvegarder
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Regenerate Weekly Forecasts Option Dialog */}
            <Dialog open={openRegenerateForecastDialog} maxWidth="md" fullWidth>
                <GenerateForecast
                    cancel={handleCancelRegenerateForecast}
                    validate={handleValidateRegenerateForecast}
                    generateForecastOption={regenerateForecastOption}
                />
            </Dialog>

            <Dialog open={openDeleteWeeklyForecastsDialog}>
                <YesNoPopup
                    message='Voulez-vous vraiment supprimer les prévisions pour la semaine actuellement selectionnée ? Cette action est irréversible'
                    yes={deleteWeeklyForecasts}
                    no={()=>setOpenDeleteWeeklyForecastsDialog(false)}
                />
            </Dialog>
        </>
    );
}

export default WeeklyShiftsForecastTable;